import type { ComponentProps, PropsWithChildren } from "react";
import { Fragment } from "react";
import { motion } from "motion/react";
import { Slot } from "radix-ui";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

export const badgeVariants = tv({
  base: "flex h-fit w-fit items-center justify-center gap-2 rounded-2xl border border-transparent text-center text-xs font-medium transition-colors duration-150",
  variants: {
    size: {
      "x-small": "px-1.5 py-0.5",
      small: "px-2 py-1",
      medium: "px-3 py-1.5",
      large: "rounded-3xl px-4 py-2.5",
    },
    color: {
      "dark-primary": "bg-primary-900 text-primary-50",
      "light-primary": "bg-primary-100 text-primary-900",
      "dark-secondary": "bg-secondary-400 text-secondary-950",
      "light-secondary": "bg-secondary-100 text-secondary-900",

      red: "bg-red-100 text-red-700",
      green: "bg-green-100 text-green-700",
      yellow: "bg-yellow-100 text-yellow-900",

      "dark-tasman": "bg-tasman-600 text-tasman-50",
      "light-tasman": "bg-tasman-200 text-tasman-700",
      "dark-merlot": "bg-merlot-800 text-merlot-50",
      "light-merlot": "bg-merlot-300 text-merlot-950",
      "dark-nutmeg": "bg-nutmeg-700 text-nutmeg-50",
      "light-nutmeg": "bg-nutmeg-200 text-nutmeg-800",
      "dark-bismark": "bg-bismark-700 text-bismark-50",
      "light-bismark": "bg-bismark-200 text-bismark-900",
    },
    outline: {
      true: "bg-transparent",
    },
  },
  compoundVariants: [
    {
      color: "dark-primary",
      outline: true,
      className: "border-neutral-900 bg-neutral-100 text-neutral-900",
    },
    {
      color: "light-primary",
      outline: true,
      className: "border-neutral-200 bg-neutral-50 text-neutral-800",
    },
    {
      color: "dark-secondary",
      outline: true,
      className: "border-secondary-100 bg-secondary-50 text-secondary-900",
    },
    {
      color: "light-secondary",
      outline: true,
      className: "border-secondary-400 bg-secondary-100 text-secondary-900",
    },

    {
      color: "red",
      outline: true,
      className: "border-red-700 bg-merlot-50 text-red-700",
    },
    {
      color: "green",
      outline: true,
      className: "border-green-700 bg-tasman-50 text-green-700",
    },
    {
      color: "yellow",
      outline: true,
      className: "border-yellow-500 bg-yellow-100 text-yellow-900",
    },

    {
      color: "dark-tasman",
      outline: true,
      className: "border-tasman-700 bg-tasman-200 text-tasman-700",
    },
    {
      color: "light-tasman",
      outline: true,
      className: "border-tasman-200 bg-tasman-50 text-tasman-600",
    },
    {
      color: "dark-merlot",
      outline: true,
      className: "border-merlot-800 bg-merlot-200 text-merlot-800",
    },
    {
      color: "light-merlot",
      outline: true,
      className: "border-merlot-300 bg-merlot-50 text-merlot-800",
    },
    {
      color: "dark-nutmeg",
      outline: true,
      className: "border-nutmeg-700 bg-nutmeg-100 text-nutmeg-700",
    },
    {
      color: "light-nutmeg",
      outline: true,
      className: "border-nutmeg-200 bg-nutmeg-50 text-nutmeg-800",
    },
    {
      color: "dark-bismark",
      outline: true,
      className: "border-bismark-700 bg-bismark-100 text-bismark-700",
    },
    {
      color: "light-bismark",
      outline: true,
      className: "border-bismark-200 bg-bismark-50 text-bismark-900",
    },
  ],
  defaultVariants: {
    size: "medium",
  },
});

type BadgeProps = ComponentProps<"div"> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean;
    animated?: boolean;
  };

const AnimatedBadge = (props: PropsWithChildren) => (
  <motion.div
    initial={{ scale: 1, filter: "brightness(1)" }}
    animate={{
      scale: [1, 1.1, 1],
      filter: ["brightness(1)", "brightness(0.9)", "brightness(1)"],
    }}
    transition={{
      duration: 1.5,
      repeat: Infinity,
      ease: "easeInOut",
    }}
    {...props}
  />
);

export const Badge = ({
  className,
  asChild,
  size,
  color,
  outline,
  animated,
  children,
  ...props
}: BadgeProps) => {
  const Component = asChild || animated ? Slot.Root : "div";

  const ChildrenWrapper = animated ? AnimatedBadge : Fragment;

  return (
    <Component
      className={badgeVariants({
        size,
        className,
        color,
        outline,
      })}
      {...props}
    >
      <ChildrenWrapper>{children}</ChildrenWrapper>
    </Component>
  );
};
